import * as stylex from '@stylexjs/stylex';

const shimmerAnim = stylex.keyframes({
    '0%': { left: '-100%' },
    '100%': { left: '100%' },
});

const MOBILE = '@media (max-width: 768px)';
const SMALL_MOBILE = '@media (max-width: 480px)';

export const styles = stylex.create({
    workspacePage: {
        maxWidth: '100%',
        marginInline: 'auto',
        padding: 0,
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: {
            default: 'var(--text-secondary)',
            ':hover': 'var(--primary)',
        },
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transitionProperty: 'color',
        transitionDuration: 'var(--transition-fast)',
    },
    pageHeader: {
        backgroundImage: `linear-gradient(135deg,
            var(--bg-card) 0%,
            rgba(59, 130, 246, 0.02) 15%,
            rgba(16, 185, 129, 0.02) 35%,
            rgba(255, 107, 107, 0.02) 55%,
            rgba(139, 92, 246, 0.02) 75%,
            var(--bg-card) 100%)`,
        borderRadius: 'var(--radius-xl)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        padding: {
            default: 'var(--spacing-2xl)',
            [MOBILE]: 'var(--spacing-xl)',
            [SMALL_MOBILE]: 'var(--spacing-lg)',
        },
        marginBottom: {
            default: 'var(--spacing-3xl)',
            [MOBILE]: 'var(--spacing-2xl)',
        },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(4px)',
        '::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(circle at 15% 40%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
                             radial-gradient(circle at 85% 60%, rgba(16, 185, 129, 0.03) 0%, transparent 50%),
                             radial-gradient(circle at 40% 20%, rgba(255, 107, 107, 0.02) 0%, transparent 50%),
                             radial-gradient(circle at 60% 80%, rgba(139, 92, 246, 0.02) 0%, transparent 50%)`,
            opacity: 1,
        },
        '::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(circle at 70% 30%, rgba(255, 193, 7, 0.01) 0%, transparent 40%),
                             radial-gradient(circle at 30% 70%, rgba(156, 39, 176, 0.01) 0%, transparent 40%)`,
            opacity: 0.8,
        },
    },
    headerContent: {
        position: 'relative',
        zIndex: 1,
    },
    pageTitle: {
        fontSize: {
            default: 'var(--font-size-3xl)',
            [MOBILE]: 'var(--font-size-2xl)',
            [SMALL_MOBILE]: 'var(--font-size-xl)',
        },
        fontWeight: 700,
        color: 'var(--text-primary)',
        marginBottom: 'var(--spacing-lg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--spacing-md)',
        flexDirection: { [MOBILE]: 'column' },
    },
    titleIcon: { color: 'var(--primary)' },
    pageSubtitle: {
        fontSize: { default: 'var(--font-size-lg)', [SMALL_MOBILE]: 'var(--font-size-md)' },
        color: 'var(--text-secondary)',
        maxWidth: '600px',
        marginInline: 'auto',
        lineHeight: 1.6,
    },
    workspaceSection: { marginBottom: 'var(--spacing-3xl)' },
    screenshotContainer: { marginBottom: 'var(--spacing-3xl)', textAlign: 'center' },
    workspaceScreenshot: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 'var(--radius-lg)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        boxShadow: 'var(--shadow-lg)',
        transitionProperty: 'transform',
        transitionDuration: 'var(--transition-normal)',
    },
    ctaSection: {
        backgroundImage: `linear-gradient(135deg, var(--bg-card) 0%, rgba(59, 130, 246, 0.02) 15%, rgba(16, 185, 129, 0.02) 35%, rgba(255, 107, 107, 0.02) 55%, rgba(139, 92, 246, 0.02) 75%, var(--bg-card) 100%)`,
        borderRadius: 'var(--radius-xl)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        padding: { default: 'var(--spacing-2xl)', [MOBILE]: 'var(--spacing-xl)', [SMALL_MOBILE]: 'var(--spacing-lg)' },
        marginBottom: 'var(--spacing-3xl)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(4px)',
        '::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(circle at 15% 40%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
                             radial-gradient(circle at 85% 60%, rgba(16, 185, 129, 0.03) 0%, transparent 50%),
                             radial-gradient(circle at 40% 20%, rgba(255, 107, 107, 0.02) 0%, transparent 50%),
                             radial-gradient(circle at 60% 80%, rgba(139, 92, 246, 0.02) 0%, transparent 50%)`,
            opacity: 1,
        },
    },
    ctaContent: { position: 'relative', zIndex: 1 },
    ctaTitle: { fontSize: 'var(--font-size-2xl)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--spacing-lg)' },
    ctaDescription: { fontSize: 'var(--font-size-lg)', color: 'var(--text-secondary)', maxWidth: '600px', marginInline: 'auto', marginBottom: 'var(--spacing-2xl)', lineHeight: 1.6 },
    ctaButtons: { display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'center', flexWrap: 'wrap', flexDirection: { [MOBILE]: 'column' }, alignItems: { [MOBILE]: 'center' } },
    primaryButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: 'var(--accent)',
        color: 'white',
        borderWidth: 0,
        paddingBlock: '0.875rem',
        paddingInline: '1.75rem',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: 600,
        textDecoration: 'none',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 1,
        transitionProperty: 'all',
        transitionDuration: '0.3s',
        cursor: 'pointer',
        transform: { ':hover': 'translateY(-3px)' },
        boxShadow: { ':hover': '0 10px 20px rgba(0, 0, 0, 0.1)' },
        width: { [MOBILE]: '100%' },
        maxWidth: { [MOBILE]: '300px' },
        justifyContent: { [MOBILE]: 'center' },
        '::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            backgroundImage: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
            zIndex: -1,
            animationName: shimmerAnim,
            animationDuration: '3s',
            animationIterationCount: 'infinite',
        },
    },
    additionalFeaturesSection: { marginBottom: 'var(--spacing-3xl)' },
    featuresGrid: { display: 'grid', gridTemplateColumns: { default: 'repeat(auto-fit, minmax(300px, 1fr))', [MOBILE]: '1fr' }, gap: 'var(--spacing-lg)' },
    featureCard: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-md)',
        padding: 'var(--spacing-lg)',
        backgroundColor: 'var(--bg-card)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: { default: 'var(--border)', ':hover': 'var(--border-hover)' },
        borderRadius: 'var(--radius-md)',
        textDecoration: 'none',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-fast)',
        position: 'relative',
        overflow: 'hidden',
        transform: { ':hover': 'translateY(-2px)' },
        boxShadow: { ':hover': 'var(--shadow-md)' },
        '::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(45deg, rgba(var(--primary-rgb, 60, 60, 60), 0.05), rgba(var(--accent-rgb, 60, 60, 60), 0.05))',
            opacity: { default: 0, ':hover': 1 },
            transitionProperty: 'opacity',
            transitionDuration: 'var(--transition-fast)',
        },
    },
    featureIcon: { width: '40px', height: '40px', backgroundColor: 'var(--primary-bg-subtle)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', flexShrink: 0 },
    featureContent: { flex: 1 },
    featureTitle: { fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text-primary)', marginBlock: '0 var(--spacing-xs)' },
    featureDescription: { color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', margin: 0, lineHeight: 1.5 },

    // FIXED: Separated styles for the arrow to be applied conditionally
    featureArrow: {
        color: 'var(--text-tertiary)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-fast)',
        transform: 'translateX(0)',
    },
    featureArrowHover: {
        color: 'var(--primary)',
        transform: 'translateX(4px)',
    },
});